import { useMemo, type FunctionComponent, type ReactElement } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { useFeatureGate } from '@atlassian/feature-gates-react';

import { useInfo } from '../../../Flow/ConsentContextProvider';
import { OAuthConsentFeatureGate } from '../../../FeatureGates/FeatureGates';
import { useMcpContext } from '../McpContextProvider';
import { bitbucketWorkspaceSelectorMessages } from './messages';
import { orgMismatchMessages } from '../orgMismatchMessages';
import { orgGroupMessages } from '../orgGroupMessages';
import MultiSelect from '../MultiSelect/MultiSelect';
import FieldLabel from '../MultiSelect/FieldLabel';
import {
  getAvatarOptionChipColor,
  getAvatarOptionIcon,
} from '../MultiSelect/avatarOption';
import { grantedNoPermissions } from '../domain/policyState';
import {
  getBitbucketWorkspaceOrgId,
  getOrgGroupHeading,
  getOrgOptionState,
  groupByOrg,
  type ItemOrgGroup,
} from '../domain/orgMatch';
import type { BitbucketWorkspace } from '#definitions/consent-info';

const BITBUCKET_SELECT_INPUT_ID =
  'oauth-consent-mcp-bitbucket-workspace-select';

/** How many Bitbucket workspaces one consent can select while multi-select is on. */
const MAX_SELECTABLE_BITBUCKET_WORKSPACES = 4;

/**
 * A workspace with the org heading it is listed under while grouping is on
 * (absent otherwise).
 */
type GroupedBitbucketWorkspace = BitbucketWorkspace & {
  orgGroup?: ItemOrgGroup;
};

// Pure functions of the item, so they live at module scope rather than being
// rebuilt in the component body.
const toValue = (ws: BitbucketWorkspace): string => ws.ari;
const getLabel = (ws: BitbucketWorkspace): string => ws.name;

/**
 * Bitbucket workspace picker for the MCP consent screen.
 *
 * Single-select by default; with site/Bitbucket multi-select on it holds up to
 * MAX_SELECTABLE_BITBUCKET_WORKSPACES workspaces, and every selection
 * in either picker has to share one organization.
 *
 * Behind MCP_GROUP_SITES_BY_ORG the options are listed under non-selectable
 * organization headings, as in the site picker, with workspaces of no named org
 * under "No organization" (see groupByOrg); a response without named orgs keeps the
 * flat list.
 *
 * Rendered below the Atlassian site selector whenever /consent/info returned at
 * least one workspace. An empty list hides the field entirely rather than
 * showing one with nothing to pick. There is no
 * `oauth-consent_bitbucket-mcp-workspace` check in this flow — see
 * useBitbucketWorkspaces. The selected workspace's ARI is submitted as
 * an additional `selectedWorkspace` param on Accept (see McpFlowParameters).
 *
 * Hidden as well whenever the Bitbucket check does not speak for the flow, i.e.
 * 'bitbucket' is absent from `getOrderedPolicySources` — which today means a
 * Trello-only token. That list is what the screen aggregates permissions over
 * and what `hasFailedPolicyCheck` / `getPrimaryPolicyErrorSource` are scoped
 * to, so a field rendered outside it would be a target the user can select and
 * submit while every permission it grants is filtered out of the screen, and a
 * failed check for it could reach neither the full-panel alert nor this field's
 * own message slot. The visibility rule is read off that same list rather than
 * off the scopes so the two cannot drift: participation otherwise depends only
 * on /consent/info's workspace list, which knows nothing about scopes. It is
 * computed once, as `showBitbucketSelector`, by getSelectorVisibility, and MCP
 * renders this field only while it is set.
 *
 * A successful check that grants nothing is reported in this field's own message
 * slot, reading only `policies.bitbucket` so a site result never surfaces here.
 * A *failed* request is shown full-panel by McpPolicyAlerts instead, and the
 * field stays mounted throughout so the selection can be changed and retried.
 */
const BitbucketWorkspaceSelector: FunctionComponent =
  (): ReactElement | null => {
    const {
      availableBitbucketWorkspaces,
      selectedBitbucketWorkspaces,
      setSelectedBitbucketWorkspaces,
      policies,
      hasOrgMismatch,
      bitbucketConstraintOrgId,
      isSiteBitbucketMultiSelectEnabled: isMulti,
      isMultiSelectionAllowed,
      isBitbucketBlockedBySandbox,
      hasSandboxConflict,
    } = useMcpContext();
    const maxSelected = isMultiSelectionAllowed
      ? MAX_SELECTABLE_BITBUCKET_WORKSPACES
      : 1;
    const { formatMessage } = useIntl();
    const info = useInfo();
    const shouldSortWorkspaces = useFeatureGate(
      OAuthConsentFeatureGate.MCP_SORT_WORKSPACES,
    );
    const shouldGroupByOrg = useFeatureGate(
      OAuthConsentFeatureGate.MCP_GROUP_SITES_BY_ORG,
    );

    // Workspaces keep their order within each org; the orgs follow the site
    // picker's sort gate, so both pickers list the orgs alike.
    const workspaces = useMemo<GroupedBitbucketWorkspace[]>(
      () =>
        shouldGroupByOrg
          ? groupByOrg(
              availableBitbucketWorkspaces,
              info,
              getBitbucketWorkspaceOrgId,
              { sortGroups: shouldSortWorkspaces },
            )
          : availableBitbucketWorkspaces,
      [
        availableBitbucketWorkspaces,
        info.availableOrganizations,
        shouldGroupByOrg,
        shouldSortWorkspaces,
      ],
    );

    // The messages below take priority over the helpers, so they gate them —
    // and only those messages are errors.
    const noPermissions = grantedNoPermissions(policies.bitbucket);
    const canShowHelp =
      !hasOrgMismatch && !hasSandboxConflict && !noPermissions;

    // Other-org workspaces stay listed but cannot be picked.
    const { isItemDisabled, helpKind } = getOrgOptionState({
      items: availableBitbucketWorkspaces,
      constraintOrgId: bitbucketConstraintOrgId,
      getOrgId: getBitbucketWorkspaceOrgId,
      hasOwnSelection: selectedBitbucketWorkspaces.length > 0,
      isMultiSelect: isMulti,
      canShowHelp,
    });

    // First rule that applies wins: errors, then the org and sandbox rules,
    // and last the default hint stating the cap and the org rule.
    const messageRules: {
      applies: boolean;
      descriptor: MessageDescriptor;
      values?: Record<string, number>;
    }[] = [
      {
        applies: hasOrgMismatch,
        descriptor: orgMismatchMessages.orgMismatch,
      },
      {
        applies: noPermissions,
        descriptor: bitbucketWorkspaceSelectorMessages.noPermissions,
      },
      {
        applies: isBitbucketBlockedBySandbox,
        descriptor: bitbucketWorkspaceSelectorMessages.sandboxBlocked,
      },
      {
        applies:
          isMulti &&
          !isMultiSelectionAllowed &&
          selectedBitbucketWorkspaces.length > 0,
        descriptor: bitbucketWorkspaceSelectorMessages.singleWorkspaceOrg,
      },
      {
        applies: helpKind === 'empty',
        descriptor: bitbucketWorkspaceSelectorMessages.noOrgWorkspaces,
      },
      {
        applies: helpKind === 'constraint',
        descriptor: bitbucketWorkspaceSelectorMessages.orgConstraint,
      },
      {
        applies: isMulti,
        descriptor: bitbucketWorkspaceSelectorMessages.multiSelectHint,
        values: { maxSelected },
      },
    ];
    const rule = messageRules.find(({ applies }) => applies);
    const message = rule && formatMessage(rule.descriptor, rule.values);
    const noOrgGroupLabel = formatMessage(orgGroupMessages.noOrganization);
    return (
      <MultiSelect<GroupedBitbucketWorkspace, string>
        inputId={BITBUCKET_SELECT_INPUT_ID}
        items={workspaces}
        value={selectedBitbucketWorkspaces.map(toValue)}
        toValue={toValue}
        getLabel={getLabel}
        getIcon={getAvatarOptionIcon}
        getChipColor={getAvatarOptionChipColor}
        getGroup={
          shouldGroupByOrg
            ? (ws) => getOrgGroupHeading(ws, noOrgGroupLabel)
            : undefined
        }
        isItemDisabled={isItemDisabled}
        isMulti={isMulti}
        maxSelected={maxSelected}
        isDisabled={isBitbucketBlockedBySandbox}
        message={message}
        messageTone={canShowHelp ? 'information' : 'error'}
        placeholder={formatMessage(
          bitbucketWorkspaceSelectorMessages.placeholder,
        )}
        renderLabel={() => (
          <FieldLabel>
            {formatMessage(bitbucketWorkspaceSelectorMessages.label)}
          </FieldLabel>
        )}
        // Store the response's own workspaces, not the grouped copies, in the
        // order they were picked.
        onChange={(aris) =>
          setSelectedBitbucketWorkspaces(
            aris.flatMap(
              (ari) =>
                availableBitbucketWorkspaces.find((ws) => ws.ari === ari) ?? [],
            ),
          )
        }
      />
    );
  };

export default BitbucketWorkspaceSelector;
